/**
 * Umami Analytics Utility
 * 
 * This file provides functions to track events and page views using Umami analytics.
 * Umami is a privacy-friendly, open-source web analytics solution.
 */

// Extend the Window interface to include umami
declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Analytics configuration
export const ANALYTICS_CONFIG = {
  WEBSITE_ID: import.meta.env.VITE_UMAMI_WEBSITE_ID,
  SRC: import.meta.env.VITE_UMAMI_SRC,
  ENABLED: !!(import.meta.env.VITE_UMAMI_WEBSITE_ID && import.meta.env.VITE_UMAMI_SRC),
  DEBUG: import.meta.env.DEV || false,
};

/**
 * Initialize Umami analytics
 */
export const initializeAnalytics = (): void => {
  if (!ANALYTICS_CONFIG.ENABLED) {
    if (ANALYTICS_CONFIG.DEBUG) {
      console.log('Umami analytics disabled - missing configuration');
    }
    return;
  }

  // Check if script is already loaded
  if (document.querySelector(`script[src="${ANALYTICS_CONFIG.SRC}"]`)) {
    if (ANALYTICS_CONFIG.DEBUG) {
      console.log('Umami script already loaded');
    }
    return;
  }

  // Create and append Umami script
  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = ANALYTICS_CONFIG.SRC;
  script.setAttribute('data-website-id', ANALYTICS_CONFIG.WEBSITE_ID);
  
  // Optional: Add data attributes for additional configuration
  script.setAttribute('data-do-not-track', 'true'); // Respect DNT header
  script.setAttribute('data-cache', 'true'); // Enable caching
  
  document.head.appendChild(script);

  if (ANALYTICS_CONFIG.DEBUG) {
    console.log('Umami analytics initialized', {
      websiteId: ANALYTICS_CONFIG.WEBSITE_ID,
      src: ANALYTICS_CONFIG.SRC,
    });
  }
};

/**
 * Track a custom event
 */
export const trackEvent = (event: string, data?: Record<string, unknown>): void => {
  if (!ANALYTICS_CONFIG.ENABLED) {
    if (ANALYTICS_CONFIG.DEBUG) {
      console.log('Analytics disabled - would track event:', event, data);
    }
    return;
  }

  if (typeof window !== 'undefined' && window.umami) {
    try {
      window.umami.track(event, data);
      if (ANALYTICS_CONFIG.DEBUG) {
        console.log('Event tracked:', event, data);
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  } else if (ANALYTICS_CONFIG.DEBUG) {
    console.log('Umami not loaded yet - would track event:', event, data);
  }
};

// Predefined tracking functions for common events
export const analytics = {
  // Authentication events
  trackLogin: (method: 'username' | 'social' = 'username') => {
    trackEvent('user-login', { method });
  },

  trackRegister: (plan?: string) => {
    trackEvent('user-register', { plan });
  },

  trackLogout: () => {
    trackEvent('user-logout');
  },

  // Navigation events
  trackPageView: (page: string) => {
    trackEvent('page-view', { page });
  },

  trackButtonClick: (button: string, location?: string) => {
    trackEvent('button-click', { button, location });
  },

  // Business events
  trackPlanSelection: (plan: string, price?: number) => {
    trackEvent('plan-selected', { plan, price });
  },

  trackPaymentAttempt: (method: 'paypal' | 'card' | 'crypto', plan: number ) => {
    trackEvent('payment-attempt', { method, plan });
  },

  trackPaymentSuccess: (method: 'paypal' | 'card' | 'crypto', plan: number ) => {
    trackEvent('payment-success', { method, plan});
  },

  trackPaymentFailed: (method: 'paypal' | 'card' | 'crypto', error: string) => {
    trackEvent('payment-failed', { method, error });
  },

  // Engagement events
  trackNewsletterSubscribe: () => {
    trackEvent('newsletter-subscribe');
  },

  trackBugReport: (type: 'bug' | 'feature') => {
    trackEvent('bug-report', { type });
  },

  trackBugVote: (type: 'bug' | 'feature', action: 'upvote' | 'downvote') => {
    trackEvent('bug-vote', { type, action });
  },

  trackContactForm: () => {
    trackEvent('contact-form-submit');
  },

  trackDownload: (file: string) => {
    trackEvent('file-download', { file });
  },

  trackExternalLink: (url: string, text?: string) => {
    trackEvent('external-link-click', { url, text });
  },

  // Feature usage
  trackFeatureUsage: (feature: string, action: string) => {
    trackEvent('feature-usage', { feature, action });
  },

  trackModalOpen: (modal: string) => {
    trackEvent('modal-open', { modal });
  },

  trackModalClose: (modal: string, duration?: number) => {
    trackEvent('modal-close', { modal, duration });
  },

  // Error tracking
  trackError: (error: string, location?: string) => {
    trackEvent('error', { error, location });
  },

  trackApiError: (endpoint: string, status: number, message: string) => {
    trackEvent('api-error', { endpoint, status, message });
  },
};

// Utility to check if analytics is ready
export const isAnalyticsReady = (): boolean => {
  return !!(ANALYTICS_CONFIG.ENABLED && typeof window !== 'undefined' && window.umami);
};

// Utility to get analytics status
export const getAnalyticsStatus = () => {
  return {
    enabled: ANALYTICS_CONFIG.ENABLED,
    loaded: isAnalyticsReady(),
    config: {
      websiteId: ANALYTICS_CONFIG.WEBSITE_ID,
      src: ANALYTICS_CONFIG.SRC,
    },
  };
};

export default analytics;
